import type { LogPage, OnboardingEvent, OnboardingMessage } from '../api-types';

export type LogKind = 'message' | 'signal';

/** One line of the log: an email or a user's signal, seen alike by the table and the cards. */
export type LogRow = {
  id: string;
  kind: LogKind;
  at: string;
  userId: string;
  profile: string | null;
  step: string | null;
  /** The text version of an email, or the signal's kind (`clicked`, `step_done`, …). */
  label: string;
  /** The status of an email; null for a signal. */
  status: string | null;
  approvedBy: string | null;
};

const fromMessage = (m: OnboardingMessage): LogRow => ({
  id: `m-${m.id}`,
  kind: 'message',
  at: m.sentAt ?? m.approvedAt ?? m.createdAt,
  userId: m.userId,
  profile: m.profile ?? null,
  step: m.step,
  label: m.textVersion,
  status: m.status,
  approvedBy: m.approvedBy,
});

const fromEvent = (e: OnboardingEvent): LogRow => ({
  id: `e-${e.id}`,
  kind: 'signal',
  at: e.at,
  userId: e.userId,
  profile: e.profile,
  step: e.step,
  label: e.kind,
  status: null,
  approvedBy: null,
});

const time = (iso: string) => {
  const value = new Date(iso).getTime();
  return Number.isNaN(value) ? 0 : value;
};

/** The two lists of a page of the log as one list, the newest first. */
export function rowsOf(page: Pick<LogPage, 'messages' | 'events'>): LogRow[] {
  return [...page.messages.map(fromMessage), ...page.events.map(fromEvent)];
}

/** Rows already shown plus a new page, without repeats, the newest first. */
export function mergeRows(shown: readonly LogRow[], more: readonly LogRow[]): LogRow[] {
  const known = new Set(shown.map((row) => row.id));
  return [...shown, ...more.filter((row) => !known.has(row.id))].sort(
    (a, b) => time(b.at) - time(a.at),
  );
}
